import React, { useEffect, useRef, useState } from 'react';
import McpAgreementDialog from '../McpAgreementDialog';
import { getApi, post, tracker } from '~/utils';
import { corpUserStore } from '~/models';
import { API, TRACKER, VIP_ERROR } from '~/constants';
import { PlatformVipAddOnKey } from '~/interfaces';
import { openUpgradeTip } from '~/container/upgrade-tip';

interface MemberApiConfig {
    memberApiKey: boolean;
    memberApiKeyLicense?: boolean;
}

interface MemberApiUpdateResponse {
    member_api_key?: boolean;
    member_api_key_license?: boolean;
}

interface AgreementResolver {
    resolve: () => void;
    reject: () => void;
}

/**
 * MCP 服务开启协议弹窗 hook。
 */
export const useMcpAgreementDialog = ({
    from
}: {
    /** 埋点参数 - 使用场景 企业设置｜开放平台 */
    from: 'profile' | 'open';
}) => {
    const [visible, setVisible] = useState(false);
    const resolverRef = useRef<AgreementResolver>();

    useEffect(() => {
        return () => {
            resolverRef.current?.reject();
            resolverRef.current = undefined;
        };
    }, []);

    const syncMemberApiModules = (data: MemberApiUpdateResponse) => {
        const { member_api_key, member_api_key_license } = data;

        if (member_api_key_license) {
            corpUserStore.setModulesKeyAndVal('member_api_key_license', true);
        }

        if (member_api_key) {
            corpUserStore.setModulesKeyAndVal('member_api_key', true);
        }
    };

    const updateMemberApiConfig = (config: MemberApiConfig) => {
        return post<MemberApiUpdateResponse>(
            getApi(API.member_api.update, corpUserStore.tenantId),
            config
        ).then((res) => {
            syncMemberApiModules(res.data);
        });
    };

    /** 开启 MCP 时同时写入协议同意态 */
    const enableMcp = () => {
        return updateMemberApiConfig({
            memberApiKey: true,
            memberApiKeyLicense: true
        });
    };

    const trackMcpServiceOn = () => {
        tracker(TRACKER.MCP_SERVICE_ON, from);
    };

    const enableMcpService = () => {
        if (!corpUserStore.hasPlatformVipFunction(PlatformVipAddOnKey.member_api)) {
            // 校验套餐
            openUpgradeTip({ bufferState: { code: VIP_ERROR.MEMBER_API } });
            return;
        }

        if (corpUserStore.isMcpAgreementAccepted) {
            // 已同意协议，直接开启；仍同时提交 license + enable
            trackMcpServiceOn();
            return enableMcp();
        }

        tracker(TRACKER.MCP_SERVICE_AGREEMENT);
        setVisible(true);
        return new Promise<void>((resolve, reject) => {
            resolverRef.current = { resolve, reject };
        });
    };

    /**
     * 静默同意协议（授权页「授权即同意」，不弹窗）。
     * 复用 enableMcp 同时写入协议与开启态；授权页入口 MCP 通常已开启。
     */
    const enableMcpWithAgreementSilently = () => {
        if (corpUserStore.isMcpAgreementAccepted) {
            return Promise.resolve();
        }
        return enableMcp();
    };

    const resetDialog = () => {
        resolverRef.current = undefined;
        setVisible(false);
    };

    const handleAgree = () => {
        return enableMcp().then(() => {
            trackMcpServiceOn();
            resolverRef.current?.resolve();
            resetDialog();
        });
    };

    const handleReject = () => {
        resolverRef.current?.reject();
        resetDialog();
    };

    const mcpAgreementDialog = (
        <McpAgreementDialog visible={visible} onAgree={handleAgree} onReject={handleReject} />
    );

    return {
        enableMcpService,
        enableMcpWithAgreementSilently,
        mcpAgreementDialog
    };
};
